import maplibregl, { type Map as MlMap } from "maplibre-gl";

/** Traffic cameras in the radius (SPEC v0.7 §4; BMA source, v0.8.0).
 *
 *  Positions come from Bangkok's OFFICIAL OPEN DATA (data.bangkok.go.th · bma-cctv,
 *  สำนักการจราจรและขนส่ง กทม.) → /data/cameras.json. The live images live only on
 *  bmatraffic.com, whose Terms of Service require WRITTEN CONSENT to copy their
 *  content and forbid bot/app pulling — and the images are HTTP-only (our page is
 *  HTTPS → mixed-content). So FlowSight does NOT relay or embed the stream: each
 *  camera links out to the BMA viewer. We never fabricate camera data. */

export interface Camera { id: string; name: string; district: string; lat: number; lon: number }
const BMA_VIEWER = "http://www.bmatraffic.com/index.aspx";
const CREDIT = "ภาพ: BMA Traffic สำนักการจราจรและขนส่ง กทม.";
let camsP: Promise<Camera[]> | null = null;
const markers: maplibregl.Marker[] = [];

/** Camera positions from the official open dataset (cached). [] on failure. */
export async function loadCameras(): Promise<Camera[]> {
  if (!camsP) {
    camsP = fetch("/data/cameras.json")
      .then((r) => (r.ok ? r.json() : { cameras: [] }))
      .then((d: { cameras?: Camera[] }) => d.cameras ?? [])
      .catch(() => []);
  }
  return camsP;
}

function haversine(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const R = 6371, d2r = Math.PI / 180;
  const dLat = (bLat - aLat) * d2r, dLon = (bLon - aLon) * d2r;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(aLat * d2r) * Math.cos(bLat * d2r) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** Cameras within the radius of the center (filter BEFORE any clustering). */
export function camerasInRadius(cams: Camera[], center: { lat: number; lon: number }, radiusKm: number): (Camera & { distKm: number })[] {
  return cams
    .map((c) => ({ ...c, distKm: haversine(center.lat, center.lon, c.lat, c.lon) }))
    .filter((c) => c.distKm <= radiusKm)
    .sort((a, b) => a.distKm - b.distKm);
}

export function clearCameraMarkers(): void { for (const m of markers) m.remove(); markers.length = 0; }

export function showCameraMarkers(map: MlMap, cams: (Camera & { distKm: number })[]): void {
  clearCameraMarkers();
  for (const c of cams) {
    const node = document.createElement("div");
    node.className = "cam-marker";
    node.textContent = "📹";
    node.title = c.name;
    node.addEventListener("click", (e) => {
      e.stopPropagation();
      new maplibregl.Popup({ closeButton: true, maxWidth: "270px", className: "fs-popup" })
        .setLngLat([c.lon, c.lat]).setHTML(cameraCardHtml(c)).addTo(map);
    });
    markers.push(new maplibregl.Marker({ element: node, anchor: "center" }).setLngLat([c.lon, c.lat]).addTo(map));
  }
}

export function cameraCardHtml(c: Camera & { distKm?: number }): string {
  const dist = c.distKm != null ? ` · ห่าง ${c.distKm.toFixed(1)} กม.` : "";
  return `<div class="pop">
    <div class="pop-row"><b>📹 ${esc(c.name)}</b></div>
    <div class="muted small">${esc(c.district ? "เขต" + c.district : "")}${dist}</div>
    <div class="muted small" style="margin-top:4px">ภาพกล้องไม่ใช่ค่าความลึกน้ำ · ภาพสดฝังในแอปไม่ได้ (เว็บ BMA ไม่ใช่ HTTPS และเงื่อนไขต้องขออนุญาต)</div>
    <div style="margin-top:8px"><a href="${BMA_VIEWER}" target="_blank" rel="noopener">เปิดดูกล้องที่เว็บ BMA Traffic ↗</a></div>
    <div class="muted small" style="margin-top:6px">${esc(CREDIT)}</div>
  </div>`;
}

function il<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, string> = {}, ...kids: (Node | string)[]): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) { if (k === "html") n.innerHTML = v; else n.setAttribute(k, v); }
  n.append(...kids); return n;
}

export interface CameraPanelOpts {
  map: MlMap;
  center: { lat: number; lon: number } | null;
  radiusKm: number;
  onExpandRadius: (km: number) => void;
  onClose: () => void;
}

/** "📹 กล้องในวง" panel. Positions only (open data) + link-out to BMA. */
export async function renderCameraPanel(host: HTMLElement, opts: CameraPanelOpts): Promise<void> {
  const wrap = (...kids: Node[]): void => {
    const close = il("button", { class: "btn ghost sm" }, "✕ ปิด");
    close.addEventListener("click", opts.onClose);
    host.replaceChildren(il("div", { class: "result card-in" }, il("div", { class: "tf-head" }, il("b", {}, "📹 กล้องจราจรในวง"), close), ...kids));
  };
  if (!opts.center) { wrap(il("div", { class: "muted small", style: "padding:6px 2px" }, "ค้นหาสถานที่ แตะแผนที่ หรือกด GPS ก่อน แล้วค่อยเปิดกล้องในวง")); return; }
  wrap(il("div", { class: "muted small" }, "กำลังโหลดตำแหน่งกล้อง (ข้อมูลเปิด กทม.)…"));

  const all = await loadCameras();
  const inRadius = camerasInRadius(all, opts.center, opts.radiusKm);
  showCameraMarkers(opts.map, inRadius);

  if (inRadius.length === 0) {
    const next = Math.min(50, Math.max(opts.radiusKm * 2, opts.radiusKm + 2));
    const btn = il("button", { class: "btn" }, `ขยายเป็น ${next} กม.`);
    btn.addEventListener("click", () => opts.onExpandRadius(next));
    wrap(il("div", {}, `ไม่พบกล้องในรัศมี ${opts.radiusKm} กม. (กล้อง BMA ครอบคลุมเฉพาะ กทม.)`),
      opts.radiusKm >= 50 ? il("div", { class: "muted small", style: "margin-top:6px" }, "ลองเปลี่ยนตำแหน่ง") : il("div", { style: "margin-top:10px" }, btn));
    return;
  }

  const rows = inRadius.map((c) => {
    const r = il("button", { class: "tf-row" },
      il("span", { class: "tf-ico" }, "📹"),
      il("span", { class: "tf-name" }, il("b", {}, c.name), il("span", { class: "muted small" }, `ห่าง ${c.distKm.toFixed(1)} กม.${c.district ? " · เขต" + c.district : ""}`)),
      il("span", { class: "muted small" }, "↗"));
    r.addEventListener("click", () => {
      opts.map.flyTo({ center: [c.lon, c.lat], zoom: 15, duration: 500 });
      new maplibregl.Popup({ closeButton: true, maxWidth: "270px", className: "fs-popup" }).setLngLat([c.lon, c.lat]).setHTML(cameraCardHtml(c)).addTo(opts.map);
    });
    return r;
  });

  wrap(
    il("div", { class: "muted small" }, `กล้องในวง: ${inRadius.length} จุด (นับตำแหน่ง · ภาพดูที่เว็บ BMA) · รัศมี ${opts.radiusKm} กม.`),
    il("div", { class: "muted small", style: "margin:2px 0 8px" }, "ภาพกล้องไม่ใช่ค่าความลึกน้ำ · ภาพสดต้องเปิดที่เว็บ BMA (ตามเงื่อนไข กทม.)"),
    il("div", { class: "tf-list" }, ...rows),
    il("div", { class: "muted small", style: "margin-top:8px" }, CREDIT),
  );
}
function esc(s: string): string { return s.replace(/[<>&"]/g, (ch) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[ch] ?? ch)); }
